/**
 * Stock containment status card on the NCR edit Detail tab
 * (customer review 2026-07).
 *
 * Shows the containment request captured at claim time (current stock
 * snapshot + OK / NOT OK quantities, editable while the NCR is editable)
 * and lets the customer confirm the stock as contained — the confirmation
 * is stamped server-side with who/when, feeding the days-to-contain KPI.
 */
import { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PackageCheck, Loader2 } from 'lucide-react';
import { qualityAPI } from '../../lib/api';
import type { NonConformanceReport } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';

interface NCRContainmentStatusCardProps {
  ncr: NonConformanceReport;
  formData: Record<string, unknown>;
  canEdit: boolean;
  /** May confirm containment: a draft's customer, or an admin with Master
   *  Edit on once the NCR is submitted (CR-8707). */
  canConfirm: boolean;
  /** CR-8707: Master Edit is on, so the confirmation is sent (and logged) as one. */
  masterEdit?: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
}

export default function NCRContainmentStatusCard({
  ncr, formData, canEdit, canConfirm, masterEdit = false, onUpdateFormData,
}: NCRContainmentStatusCardProps) {
  const { formatDate } = useFormatters();
  const [confirming, setConfirming] = useState(false);

  // Confirmation persists immediately (like collection/credit confirmations)
  // so the who/when audit stamp lands even when the NCR is past its editable
  // stage and the Save button is hidden.
  const handleConfirmToggle = async (value: boolean) => {
    setConfirming(true);
    try {
      await qualityAPI.updateNCR(ncr.id, {
        containment_confirmed: value,
        ...(masterEdit ? { master_edit: true } : {}),
      } as Partial<NonConformanceReport>);
      onUpdateFormData('containment_confirmed', value);
      toast.success(value ? 'Stock confirmed as contained' : 'Containment confirmation removed');
    } catch (err: unknown) {
      console.error('Failed to update containment confirmation:', err);
      toast.error('Failed to update containment confirmation');
    } finally {
      setConfirming(false);
    }
  };
  const required = (formData['containment_required'] as boolean | undefined) ?? ncr.containment_required ?? false;
  const confirmed = (formData['containment_confirmed'] as boolean | undefined) ?? ncr.containment_confirmed ?? false;
  const currentStock = (formData['containment_current_stock'] as string | undefined) ?? (ncr.containment_current_stock ?? '');
  const qtyOk = (formData['containment_qty_ok'] as string | undefined) ?? (ncr.containment_qty_ok ?? '');
  const qtyNotOk = (formData['containment_qty_not_ok'] as string | undefined) ?? (ncr.containment_qty_not_ok ?? '');

  return (
    <div className="border-t pt-4 mt-2">
      <h4 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <PackageCheck className="h-4 w-4 text-emerald-600" /> Stock Containment
        {required && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            confirmed ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
          }`}>
            {confirmed ? 'Contained' : 'Requested'}
          </span>
        )}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
        <div>
          <Label>Containment requested?</Label>
          <select
            value={required ? 'yes' : 'no'}
            onChange={(e) => onUpdateFormData('containment_required', e.target.value === 'yes')}
            disabled={!canEdit}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
        {required && (
          <>
            {/* The containment quantities are captured by the customer when
                performing containment, so they stay editable during the
                containment step (canConfirm), not only at NCR-detail edit
                time (canEdit). Current Stock is manual entry (no ERP feed). */}
            <div>
              <Label>Current Stock (at claim)</Label>
              <Input
                type="number"
                min="0"
                value={String(currentStock)}
                onChange={(e) => onUpdateFormData('containment_current_stock', e.target.value)}
                disabled={!(canEdit || canConfirm)}
              />
            </div>
            <div>
              <Label>OK Qty</Label>
              <Input
                type="number"
                min="0"
                value={String(qtyOk)}
                onChange={(e) => onUpdateFormData('containment_qty_ok', e.target.value)}
                disabled={!(canEdit || canConfirm)}
              />
            </div>
            <div>
              <Label>NOT OK Qty</Label>
              <Input
                type="number"
                min="0"
                value={String(qtyNotOk)}
                onChange={(e) => onUpdateFormData('containment_qty_not_ok', e.target.value)}
                disabled={!(canEdit || canConfirm)}
              />
            </div>
          </>
        )}
      </div>
      {required && (
        <div className="mt-3">
          <label className={`flex items-center gap-2 text-sm font-medium ${!canConfirm || confirming ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => handleConfirmToggle(e.target.checked)}
              disabled={!canConfirm || confirming}
              className="disabled:cursor-not-allowed"
            />
            Stock confirmed as 100% contained
            {confirming && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </label>
          {ncr.containment_confirmed && ncr.containment_confirmed_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Confirmed by {ncr.containment_confirmed_by_name || '—'} on {formatDate(ncr.containment_confirmed_at)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
